import { useEffect, useState } from 'react'
import { Autocomplete, CircularProgress, TextField } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { popularityApi } from '@api/endpoints/popularity'
import type { PopularityItemOptionDto, PopularityItemType } from '@api/types/popularity'

interface PopularityItemPickerProps {
  itemType: PopularityItemType
  value: PopularityItemOptionDto | null
  onChange: (item: PopularityItemOptionDto | null) => void
  disabled?: boolean
}

/** Adventure (title or id) / Story (name) search for "Generate sample data". */
export function PopularityItemPicker({ itemType, value, onChange, disabled }: PopularityItemPickerProps) {
  const [input, setInput] = useState('')
  const [query, setQuery] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setQuery(input.trim()), 300)
    return () => clearTimeout(t)
  }, [input])

  const { data, isFetching } = useQuery({
    queryKey: ['popularity-items', itemType, query],
    queryFn: () => popularityApi.searchItems(itemType, query),
    staleTime: 60_000,
  })

  return (
    <Autocomplete
      options={data ?? []}
      value={value}
      onChange={(_, v) => onChange(v)}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      getOptionLabel={(o) => o.title}
      isOptionEqualToValue={(a, b) => a.itemId === b.itemId}
      filterOptions={(x) => x}
      loading={isFetching}
      disabled={disabled}
      noOptionsText={query ? 'Nothing matches' : 'Type to search'}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={itemType === 'story' ? 'Story' : 'Adventure (title or id)'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}
